import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@components/layout/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { SuperAdminProtectedRoute } from '@features/superadmin/SuperAdminProtectedRoute';
import { FullPageSpinner } from '@components/ui';
import { RouteErrorBoundary } from '@components/error/RouteErrorBoundary';

const LoginPage = lazy(() => import('@features/auth/LoginPage'));
const RegisterPage = lazy(() => import('@features/auth/RegisterPage'));
const PublicOnboardPage = lazy(() => import('@features/onboarding/PublicOnboardPage'));
const PublicApplyPage = lazy(() => import('@features/recruitment/PublicApplyPage'));
const RecruitmentHubLayout = lazy(() => import('@features/recruitment/pages/RecruitmentHubLayout'));
const RecruitmentOverview = lazy(() => import('@features/recruitment/pages/RecruitmentOverview'));
const FormsListPage = lazy(() => import('@features/recruitment/pages/FormsListPage'));
const FormBuilderPage = lazy(() => import('@features/recruitment/pages/FormBuilderPage'));
const ApplicationsBoardPage = lazy(() => import('@features/recruitment/pages/ApplicationsBoardPage'));
const ApplicantTagsPage = lazy(() => import('@features/recruitment/pages/ApplicantTagsPage'));
// Supervisor Portal (employee-facing, public routes) + admin approval page.
const PortalLoginPage = lazy(() => import('@features/supervisorPortal/PortalLoginPage'));
const PortalProtectedRoute = lazy(() => import('@features/supervisorPortal/PortalProtectedRoute'));
const PortalShell = lazy(() => import('@features/supervisorPortal/PortalShell'));
const PortalTeamPage = lazy(() => import('@features/supervisorPortal/pages/TeamPage'));
const PortalMyRequestsPage = lazy(() => import('@features/supervisorPortal/pages/MyRequestsPage'));
const PortalEmployeeProfilePage = lazy(() => import('@features/supervisorPortal/pages/EmployeeProfilePage'));
const PortalEmployeeReportPage = lazy(() => import('@features/supervisorPortal/pages/EmployeeReportPage'));
const PortalEmployeeSummaryReportPage = lazy(
  () => import('@features/supervisorPortal/pages/EmployeeSummaryReportPage'),
);
const PortalTeamReportPage = lazy(() => import('@features/supervisorPortal/pages/TeamReportPage'));
const PortalAttendanceReportPage = lazy(
  () => import('@features/supervisorPortal/pages/PortalAttendanceReportPage'),
);
const PortalLoanApprovalsPage = lazy(() => import('@features/supervisorPortal/pages/LoanApprovalsPage'));
const SupervisorRequestsPage = lazy(() => import('@features/supervisorRequests/SupervisorRequestsPage'));
const ComplaintsPage = lazy(() => import('@features/complaints/ComplaintsPage'));
const NotificationTemplatesPage = lazy(
  () => import('@features/notificationTemplates/NotificationTemplatesPage'),
);
const SurveysListPage = lazy(() => import('@features/surveys/SurveysListPage'));
const SurveyBuilderPage = lazy(() => import('@features/surveys/SurveyBuilderPage'));
const SurveyStatsPage = lazy(() => import('@features/surveys/SurveyStatsPage'));
const PublicSurveyPage = lazy(() => import('@features/surveys/PublicSurveyPage'));
const OnboardingLinksPage = lazy(() => import('@features/onboarding/OnboardingLinksPage'));
const OnboardingSubmissionsPage = lazy(
  () => import('@features/onboarding/OnboardingSubmissionsPage'),
);
const SuperAdminLoginPage = lazy(() => import('@features/superadmin/SuperAdminLoginPage'));
const SuperAdminHubLayout = lazy(
  () => import('@features/superadmin/components/SuperAdminHubLayout'),
);
const SuperAdminDashboardPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminDashboardPage'),
);
const SuperAdminCompaniesPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminCompaniesPage'),
);
const SuperAdminAgentsPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminAgentsPage'),
);
const SuperAdminServersPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminServersPage'),
);
const SuperAdminAuditLogsPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminAuditLogsPage'),
);
const SuperAdminPaymentsPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminPaymentsPage'),
);
const SuperAdminWhatsappBotPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminWhatsappBotPage'),
);
const SuperAdminTelegramBotPage = lazy(
  () => import('@features/superadmin/pages/SuperAdminTelegramBotPage'),
);
const SaRedirectPage = lazy(() => import('@features/superadmin/pages/SaRedirectPage'));
// Embedded SSO landing (partner iframe): /app/embed?t=<mintedToken>
const EmbedRedirectPage = lazy(() => import('@features/embed/EmbedRedirectPage'));
const DashboardPage = lazy(() => import('@features/dashboard/DashboardPage'));
// Dashboard v2 — modern bento-grid redesign, served at /dashboard/v2 alongside
// the canonical dashboard so the design can be reviewed without disrupting users.
const DashboardV2Page = lazy(() => import('@features/dashboard/DashboardV2Page'));
// Dashboard v3 — "mahir" design-spec implementation (purple/beige card layout,
// Tajawal + IBM Plex Sans Arabic), served at /dashboard/v3.
const DashboardV3Page = lazy(() => import('@features/dashboard/DashboardV3Page'));
const RealtimePage = lazy(() => import('@features/realtime/RealtimePage'));
const NewsPage = lazy(() => import('@features/news/NewsPage'));
const NotificationsPendingPage = lazy(
  () => import('@features/notifications/NotificationsPendingPage'),
);
// Push notification system (new) — three routes:
//   /notifications              → center for the current user
//   /notifications/broadcast    → manager-only composer
//   /notifications/broadcasts   → manager-only history
const NotificationCenterPage = lazy(
  () => import('@features/notifications/pages/NotificationCenterPage'),
);
const BroadcastComposerPage = lazy(
  () => import('@features/notifications/pages/BroadcastComposerPage'),
);
const BroadcastsHistoryPage = lazy(
  () => import('@features/notifications/pages/BroadcastsHistoryPage'),
);
const MonitoringPage = lazy(() => import('@features/monitoring/MonitoringPage'));
const HealthPage = lazy(() => import('@features/health/HealthPage'));
const LicenseActivatePage = lazy(() => import('@features/license/LicenseActivatePage'));
const ProfilePage = lazy(() => import('@features/account/ProfilePage'));
const SessionsPage = lazy(() => import('@features/account/SessionsPage'));
const LoginHistoryPage = lazy(() => import('@features/account/LoginHistoryPage'));
const PermissionsPage = lazy(() => import('@features/account/PermissionsPage'));
const EmployeesListPage = lazy(() => import('@features/employees/EmployeesListPage'));
const EmployeeFormPage = lazy(() => import('@features/employees/EmployeeFormPage'));
const UsersListPage = lazy(() => import('@features/users/UsersListPage'));
const VisitorsHubLayout = lazy(() => import('@features/visitors/pages/VisitorsHubLayout'));
const VisitPassesPage = lazy(() => import('@features/visitors/pages/VisitPassesPage'));
const VisitorEntriesPage = lazy(() => import('@features/visitors/pages/VisitorEntriesPage'));
const PublicVisitPassPage = lazy(() => import('@features/visitors/PublicVisitPassPage'));
const TagsPage = lazy(() => import('@features/tags/TagsPage'));
const AttendancePage = lazy(() => import('@features/attendance/AttendancePage'));
const AuditLogPage = lazy(() => import('@features/auditLog/AuditLogPage'));
const InboundWebhookLogPage = lazy(
  () => import('@features/settings/pages/InboundWebhookLogPage'),
);
// ApiRequestsPage is now mounted as a tab inside DevicesHubPage.
// Students Hub — URL-based tabs (/students/<tab>). The old /students?tab=...
// query param is kept working via a redirect inside the layout.
const StudentsHubLayout = lazy(
  () => import('@features/studentsAttendance/components/StudentsHubLayout'),
);
const StudentsOverview = lazy(
  () => import('@features/studentsAttendance/pages/StudentsOverview'),
);
const StudentAttendancePage = lazy(
  () => import('@features/studentsAttendance/StudentAttendancePage'),
);
const StudentAttendanceReportPage = lazy(
  () => import('@features/studentsAttendance/StudentAttendanceReportPage'),
);
const LectureReportPage = lazy(() => import('@features/attendance/LectureReportPage'));
const QrAttendancePage = lazy(() => import('@features/qr/QrAttendancePage'));
const SuddenFingerprintsPage = lazy(() => import('@features/suddenFingerprints/SuddenFingerprintsPage'));
const LocationViolationsPage = lazy(
  () => import('@features/locationViolations/LocationViolationsPage'),
);
// Devices Hub — URL-based tabs (/devices/<tab>). Each child is its own
// lazy chunk so the bundle for the hub page itself stays tiny.
const DevicesHubLayout = lazy(
  () => import('@features/devices/components/hub/DevicesHubLayout'),
);
const DevicesOverview = lazy(
  () => import('@features/devices/pages/DevicesOverview'),
);
const DevicesPage = lazy(() => import('@features/devices/DevicesPage'));
const DevicesOfflinePage = lazy(() => import('@features/devices/DevicesOfflinePage'));
const DevicesMatrixPage = lazy(() => import('@features/devices/DevicesMatrixPage'));
const DeviceEmployeesPage = lazy(() => import('@features/devices/DeviceEmployeesPage'));
// Device User Balancing ("موازنة المستخدمين بين الأجهزة") — standalone admin/devices
// page (settings + device grouping + the sync-operations report). Top-level route
// so the report has full width; reachable via the Devices hub sub-nav.
const DeviceBalancingPage = lazy(
  () => import('@features/deviceBalancing/DeviceBalancingPage'),
);
// Employee Evaluation ("تقييم الموظفين") — a small tabbed hub (records +
// event-type catalog + settings). Phase 1 records events as the source of
// truth; financial effects and letters land in later phases behind approval.
const EvaluationHubLayout = lazy(
  () => import('@features/evaluation/EvaluationHubLayout'),
);
const EvaluationRecordsPage = lazy(
  () => import('@features/evaluation/EvaluationRecordsPage'),
);
const EvaluationEventTypesPage = lazy(
  () => import('@features/evaluation/EvaluationEventTypesPage'),
);
const EvaluationSettingsPage = lazy(
  () => import('@features/evaluation/EvaluationSettingsPage'),
);
const EvaluationLeaderboardPage = lazy(
  () => import('@features/evaluation/EvaluationLeaderboardPage'),
);
const EvaluationLettersPage = lazy(
  () => import('@features/evaluation/EvaluationLettersPage'),
);
const EvaluationTemplatesPage = lazy(
  () => import('@features/evaluation/EvaluationTemplatesPage'),
);
const EvaluationJourneyPage = lazy(
  () => import('@features/evaluation/EvaluationJourneyPage'),
);
const NetworksPage = lazy(() => import('@features/networks/pages/NetworksPage'));
const BeaconsPage = lazy(() => import('@features/beacons/pages/BeaconsPage'));
const BeaconAttemptsPage = lazy(() => import('@features/beacons/pages/AttemptsPage'));
const UnregisteredEmployeesPage = lazy(
  () => import('@features/employees/pages/UnregisteredEmployeesPage'),
);
const OperationsPage = lazy(() => import('@features/operations/OperationsPage'));
const ApiRequestsPage = lazy(() => import('@features/apiRequests/ApiRequestsPage'));
const LiveLocationsPage = lazy(() => import('@features/liveLocations/LiveLocationsPage'));
const WatcherPage = lazy(() => import('@features/watcher/WatcherPage'));
const LocationPickerRoute = lazy(() => import('@features/locationPicker/LocationPickerRoute'));
const RulesHubLayout = lazy(() => import('@features/rules/components/RulesHubLayout'));
const RulesOverviewPage = lazy(() => import('@features/rules/pages/RulesOverviewPage'));
const AttendanceRulesPage = lazy(() => import('@features/rules/pages/AttendanceRulesPage'));
const AttendanceRuleFormPage = lazy(() => import('@features/rules/pages/AttendanceRuleFormPage'));
const RuleGroupsPage = lazy(() => import('@features/rules/pages/RuleGroupsPage'));
const RuleGroupFormPage = lazy(() => import('@features/rules/pages/RuleGroupFormPage'));
const AlertConditionsPage = lazy(() => import('@features/rules/pages/AlertConditionsPage'));
const GenericRulesPage = lazy(() => import('@features/rules/pages/GenericRulesPage'));
const FlexibleAggregateRulesPage = lazy(
  () => import('@features/rules/pages/FlexibleAggregateRulesPage'),
);
const EventOverrideRulesPage = lazy(
  () => import('@features/rules/pages/EventOverrideRulesPage'),
);
const EnrollSwapRulesPage = lazy(() => import('@features/rules/pages/EnrollSwapRulesPage'));
const ExtraMinutesPage = lazy(() => import('@features/rules/pages/ExtraMinutesPage'));
const TimeBasedLeavesPage = lazy(() => import('@features/rules/pages/TimeBasedLeavesPage'));
const TimeLeaveTypesPage = lazy(
  () => import('@features/timeLeaveTypes/pages/TimeLeaveTypesPage'),
);
const TimeLeaveSettingsPage = lazy(
  () => import('@features/timeLeaveTypes/pages/TimeLeaveSettingsPage'),
);
const RulesGuidePage = lazy(() => import('@features/rules/pages/RulesGuidePage'));
// Action Policies ("إجراءات وشروط") — list + 4-step builder + runs/proposals.
const ActionPoliciesListPage = lazy(
  () => import('@features/action-policies/pages/ActionPoliciesListPage'),
);
const ActionPolicyBuilderPage = lazy(
  () => import('@features/action-policies/pages/ActionPolicyBuilderPage'),
);
const ActionPolicyRunsPage = lazy(
  () => import('@features/action-policies/pages/ActionPolicyRunsPage'),
);
const ActionPolicyLedgerPage = lazy(
  () => import('@features/action-policies/pages/ActionPolicyLedgerPage'),
);
const PromotionsListPage = lazy(
  () => import('@features/promotions/pages/PromotionsListPage'),
);
const PromotionBuilderPage = lazy(
  () => import('@features/promotions/pages/PromotionBuilderPage'),
);
const PromotionInboxPage = lazy(
  () => import('@features/promotions/pages/PromotionInboxPage'),
);
const EmployeeImportExcelPage = lazy(
  () => import('@features/employees/pages/EmployeeImportExcelPage'),
);
const EmployeeImportZkPage = lazy(
  () => import('@features/employees/pages/EmployeeImportZkPage'),
);
// Wave I.2.a — ZK ↔ HR Intake page (BIO-REQ-115..200). Now mounted as a tab
// inside the Devices hub at `/devices/zk-import`; the legacy
// `/employees/zk-intake` URL is preserved as a redirect, and a conditional
// sidebar shortcut shows when the operator owns at least one ADMS device.
const ZkIntakePage = lazy(() => import('@features/employees/pages/ZkIntakePage'));
// UnregisteredEmployeesPage is now a tab inside DevicesHubPage.
const EmployeeRecordsPage = lazy(
  () => import('@features/employees/pages/EmployeeRecordsPage'),
);
const ScheduleHistoryPage = lazy(
  () => import('@features/employees/pages/ScheduleHistoryPage'),
);
const EmployeeSnapshotPage = lazy(
  () => import('@features/employees/pages/EmployeeSnapshotPage'),
);
const EmployeeOvertimeSlotsPage = lazy(
  () => import('@features/employees/pages/EmployeeOvertimeSlotsPage'),
);
const ShiftsListPage = lazy(() => import('@features/shifts/pages/ShiftsListPage'));
const ShiftFormPage = lazy(() => import('@features/shifts/pages/ShiftFormPage'));
const TimeSlotsListPage = lazy(() => import('@features/timeSlots/pages/TimeSlotsListPage'));
const TimeSlotFormPage = lazy(() => import('@features/timeSlots/pages/TimeSlotFormPage'));
// Schedule Exceptions ("جدول الطوارئ") — temporary schedule overrides applied
// to a date range, scoped to employee/tag/department/branch/org. Lives inside
// the Shifts & Time hub at /work-time/schedule-exceptions but the form pages
// keep their own /schedule-exceptions/{new|:id|:id/edit} URLs so internal
// navigation (and bookmarks) stay clean.
const ScheduleExceptionsListPage = lazy(
  () => import('@features/scheduleExceptions/pages/ScheduleExceptionsListPage'),
);
const ScheduleExceptionFormPage = lazy(
  () => import('@features/scheduleExceptions/pages/ScheduleExceptionFormPage'),
);
const ScheduleExceptionDetailPage = lazy(
  () => import('@features/scheduleExceptions/pages/ScheduleExceptionDetailPage'),
);
const LeavesListPage = lazy(() => import('@features/leaves/pages/LeavesListPage'));
const LeaveTypesListPage = lazy(() => import('@features/leaves/pages/LeaveTypesListPage'));
const AttendanceManagementHubLayout = lazy(
  () => import('@features/attendanceManagement/components/AttendanceManagementHubLayout'),
);
const AttendanceManagementOverview = lazy(
  () => import('@features/attendanceManagement/pages/AttendanceManagementOverview'),
);
const WorkTimeHubLayout = lazy(
  () => import('@features/workTime/components/WorkTimeHubLayout'),
);
const WorkTimeOverview = lazy(
  () => import('@features/workTime/pages/WorkTimeOverview'),
);
const StaffHubLayout = lazy(
  () => import('@features/staffManagement/components/StaffHubLayout'),
);
const StaffOverview = lazy(
  () => import('@features/staffManagement/pages/StaffOverview'),
);
// Organization hub was removed — its individual pages now live inside
// other hubs (Staff / Students / Rules), but their files still ship from
// the legacy `@features/organization` folder for now.
const DepartmentsPage = lazy(() => import('@features/organization/pages/DepartmentsPage'));
const BranchesPage = lazy(() => import('@features/organization/pages/BranchesPage'));
const ProjectsPage = lazy(() => import('@features/organization/pages/ProjectsPage'));
const SubjectsPage = lazy(() => import('@features/organization/pages/SubjectsPage'));
const StagesPage = lazy(() => import('@features/organization/pages/StagesPage'));
const ReportsHubLayout = lazy(() => import('@features/reports/components/ReportsHubLayout'));
const ReportsOverview = lazy(() => import('@features/reports/pages/ReportsOverview'));
const MasterAttendanceReportPage = lazy(
  () => import('@features/reports/pages/MasterAttendanceReportPage'),
);
const DetailedAttendanceLogPage = lazy(
  () => import('@features/reports/pages/DetailedAttendanceLogPage'),
);
const MonthlyAttendanceReportPage = lazy(
  () => import('@features/reports/pages/MonthlyAttendanceReportPage'),
);
const DepartmentCapacityReportPage = lazy(
  () => import('@features/reports/pages/DepartmentCapacityReportPage'),
);
const StudentsReportsSummaryPage = lazy(
  () => import('@features/reports/pages/StudentsReportsSummaryPage'),
);
const EmployeeSummaryReportPage = lazy(
  () => import('@features/reports/pages/EmployeeSummaryReportPage'),
);
const SalarySlipsPage = lazy(() => import('@features/reports/pages/SalarySlipsPage'));
const LoansReportPage = lazy(() => import('@features/reports/pages/LoansReportPage'));
const RewardsPenaltiesPage = lazy(
  () => import('@features/reports/pages/RewardsPenaltiesPage'),
);
const PrintViewPage = lazy(() => import('@features/reports/pages/PrintViewPage'));
const SettingsHubLayout = lazy(
  () => import('@features/settings/components/SettingsHubLayout'),
);
const SettingsOverview = lazy(() => import('@features/settings/pages/SettingsOverview'));
const CompanySettingsPage = lazy(
  () => import('@features/settings/pages/CompanySettingsPage'),
);
const NotificationsPage = lazy(() => import('@features/settings/pages/NotificationsPage'));
const ScheduleTemplatesListPage = lazy(
  () => import('@features/settings/pages/ScheduleTemplatesListPage'),
);
const BackupPage = lazy(() => import('@features/settings/pages/BackupPage'));
const CustomFieldsPage = lazy(() => import('@features/settings/pages/CustomFieldsPage'));
const HolidaysPage = lazy(() => import('@features/settings/pages/HolidaysPage'));
const AiTenantSettingsPage = lazy(
  () => import('@features/settings/pages/AiTenantSettingsPage'),
);
const PdfDesignsListPage = lazy(
  () => import('@features/settings/pages/PdfDesignsListPage'),
);
const PdfDesignFormPage = lazy(
  () => import('@features/settings/pages/PdfDesignFormPage'),
);
const TranslationsPage = lazy(
  () => import('@features/settings/pages/TranslationsPage'),
);
const LegacyImportPage = lazy(
  () => import('@features/legacy-import/pages/LegacyImportPage'),
);
const BillingPage = lazy(() => import('@features/billing/pages/BillingPage'));
const BillingSuccessPage = lazy(() => import('@features/billing/pages/BillingSuccessPage'));
const BillingCancelPage = lazy(() => import('@features/billing/pages/BillingCancelPage'));
const ForbiddenPage = lazy(() => import('@components/error/ForbiddenPage'));
// Quick Access (Odoo-style icon launcher screens).
const HomeGate = lazy(() => import('@features/quick-access/HomeGate'));
const QuickAccessLauncherPage = lazy(() => import('@features/quick-access/QuickAccessLauncherPage'));
const QuickAccessViewPage = lazy(() => import('@features/quick-access/QuickAccessViewPage'));
const QuickAccessListPage = lazy(() => import('@features/quick-access/QuickAccessListPage'));
const QuickAccessBuilderPage = lazy(() => import('@features/quick-access/QuickAccessBuilderPage'));

function NotFound() {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="text-center">
        <div className="text-6xl font-bold text-brand mb-3">404</div>
        <p className="text-fg-muted">{isRtl ? 'الصفحة غير موجودة' : 'Page not found'}</p>
      </div>
    </div>
  );
}

const suspense = (node: React.ReactNode) => <Suspense fallback={<FullPageSpinner />}>{node}</Suspense>;

// Lazy-loaded AccountLayout wrapper to keep the layout out of the main chunk
const AccountLayoutLazy = lazy(() =>
  import('@features/account/AccountLayout').then((m) => ({ default: m.AccountLayout })),
);

const router = createBrowserRouter(
  [
    { path: '/login', element: suspense(<LoginPage />) },
    { path: '/register', element: suspense(<RegisterPage />) },
    // Public employee self-onboarding form (no auth, no app shell). Shareable URL
    // is /app/onboard/<token>. The backend validates the token + open/closed state.
    { path: '/onboard/:token', element: suspense(<PublicOnboardPage />) },
    // Public recruitment application form (no auth, no app shell). URL: /app/apply/<token>.
    { path: '/apply/:token', element: suspense(<PublicApplyPage />) },
    // Public employee survey (no auth, no app shell). Shareable URL: /app/survey/<token>.
    { path: '/survey/:token', element: suspense(<PublicSurveyPage />) },
    // Public visitor pass (no auth, no app shell). Shareable URL: /app/visit/<token>.
    { path: '/visit/:token', element: suspense(<PublicVisitPassPage />) },
    // Supervisor Portal — employee-facing (separate employee auth, no AppShell).
    { path: '/portal/login', element: suspense(<PortalLoginPage />) },
    {
      path: '/portal',
      element: suspense(<PortalProtectedRoute />),
      children: [
        {
          element: suspense(<PortalShell />),
          children: [
            { index: true, element: suspense(<PortalTeamPage />) },
            { path: 'requests', element: suspense(<PortalMyRequestsPage />) },
            { path: 'report', element: suspense(<PortalTeamReportPage />) },
            { path: 'attendance', element: suspense(<PortalAttendanceReportPage />) },
            { path: 'loan-approvals', element: suspense(<PortalLoanApprovalsPage />) },
            { path: 'employee/:id', element: suspense(<PortalEmployeeProfilePage />) },
            { path: 'employee/:id/report', element: suspense(<PortalEmployeeReportPage />) },
            { path: 'employee/:id/summary', element: suspense(<PortalEmployeeSummaryReportPage />) },
          ],
        },
      ],
    },
    { path: '/superadmin-login', element: suspense(<SuperAdminLoginPage />) },
    {
      path: '/location-picker',
      element: (
        <ProtectedRoute>
          {suspense(<LocationPickerRoute />)}
        </ProtectedRoute>
      ),
    },
    {
      path: '/billing/success',
      element: <ProtectedRoute>{suspense(<BillingSuccessPage />)}</ProtectedRoute>,
    },
    {
      path: '/billing/cancel',
      element: <ProtectedRoute>{suspense(<BillingCancelPage />)}</ProtectedRoute>,
    },
    {
      path: '/sa-redirect',
      element: suspense(<SaRedirectPage />),
    },
    // Embedded SSO landing — no auth wrapper; the page signs in from the URL token.
    {
      path: '/embed',
      element: suspense(<EmbedRedirectPage />),
    },
    {
      path: '/superadmin',
      element: (
        <SuperAdminProtectedRoute>
          {suspense(<SuperAdminHubLayout />)}
        </SuperAdminProtectedRoute>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        { index: true, element: suspense(<SuperAdminDashboardPage />) },
        { path: 'companies', element: suspense(<SuperAdminCompaniesPage />) },
        { path: 'agents', element: suspense(<SuperAdminAgentsPage />) },
        { path: 'servers', element: suspense(<SuperAdminServersPage />) },
        { path: 'audit-logs', element: suspense(<SuperAdminAuditLogsPage />) },
        { path: 'payments', element: suspense(<SuperAdminPaymentsPage />) },
        { path: 'whatsapp-bot', element: suspense(<SuperAdminWhatsappBotPage />) },
        { path: 'telegram-bot', element: suspense(<SuperAdminTelegramBotPage />) },
      ],
    },
    {
      path: '/monitoring',
      element: (
        <ProtectedRoute permission="showDashboard">
          {suspense(<MonitoringPage />)}
        </ProtectedRoute>
      ),
    },
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        // Landing gate: sends the user to their assigned Quick Access "home"
        // screen if one exists, else to the dashboard.
        { index: true, element: suspense(<HomeGate />) },
        // Quick Access — runtime launcher + a single screen view (any authed user).
        { path: 'quick-access', element: <ProtectedRoute>{suspense(<QuickAccessLauncherPage />)}</ProtectedRoute> },
        { path: 'quick-access/view/:id', element: <ProtectedRoute>{suspense(<QuickAccessViewPage />)}</ProtectedRoute> },
        // Quick Access — admin builder (gated by the quickAccess.view permission).
        { path: 'quick-access/manage', element: <ProtectedRoute permission="showQuickAccess">{suspense(<QuickAccessListPage />)}</ProtectedRoute> },
        { path: 'quick-access/manage/new', element: <ProtectedRoute permission="showQuickAccess">{suspense(<QuickAccessBuilderPage />)}</ProtectedRoute> },
        { path: 'quick-access/manage/:id', element: <ProtectedRoute permission="showQuickAccess">{suspense(<QuickAccessBuilderPage />)}</ProtectedRoute> },
        // V3 ("mahir" design-spec) is now the default dashboard — every link to
        // `/dashboard` (post-login redirect, `/` index, sidebar) lands here.
        { path: 'dashboard', element: suspense(<DashboardV3Page />) },
        // Previous default dashboard, preserved for reference / fallback.
        { path: 'dashboard/v1', element: suspense(<DashboardPage />) },
        // Modern V2 dashboard preview — full bento layout, branded hero,
        // sparkline KPIs, donut + center KPI, live activity timeline.
        { path: 'dashboard/v2', element: suspense(<DashboardV2Page />) },
        // mahir design-spec dashboard (explicit alias of the new default).
        { path: 'dashboard/v3', element: suspense(<DashboardV3Page />) },
        // News moved into the Students hub. Old URL kept as a redirect.
        { path: 'news', element: <Navigate to="/students/news" replace /> },
        // Moved into the Shifts & Time hub. Old URLs kept as redirects so
        // bookmarks and external links keep landing on the right page.
        { path: 'realtime', element: <Navigate to="/work-time/realtime" replace /> },
        {
          path: 'notifications-pending',
          element: <Navigate to="/work-time/notifications-pending" replace />,
        },
        // New push-notification system routes (separate from the legacy
        // "notifications-pending" fingerprint queue above). The center is
        // open to any authenticated user with `showNotifications`; the
        // broadcast composer + history are manager-only via `sendBroadcast`.
        {
          path: 'notifications',
          element: (
            <ProtectedRoute permission="showNotifications">
              {suspense(<NotificationCenterPage />)}
            </ProtectedRoute>
          ),
        },
        {
          path: 'notifications/broadcast',
          element: (
            <ProtectedRoute permission="sendBroadcast">
              {suspense(<BroadcastComposerPage />)}
            </ProtectedRoute>
          ),
        },
        {
          path: 'notifications/broadcasts',
          element: (
            <ProtectedRoute permission="sendBroadcast">
              {suspense(<BroadcastsHistoryPage />)}
            </ProtectedRoute>
          ),
        },
        { path: '_health', element: suspense(<HealthPage />) },
        { path: 'license-activate', element: suspense(<LicenseActivatePage />) },
        {
          path: 'account',
          element: suspense(<AccountLayoutLazy />),
          children: [
            { index: true, element: <Navigate to="/account/profile" replace /> },
            { path: 'profile', element: suspense(<ProfilePage />) },
            { path: 'sessions', element: suspense(<SessionsPage />) },
            { path: 'login-history', element: suspense(<LoginHistoryPage />) },
            { path: 'permissions', element: suspense(<PermissionsPage />) },
          ],
        },
        {
          path: 'employees',
          children: [
            // List + management pages moved into the Staff hub. Form pages
            // (`/employees/new`, `/employees/:id`) stay at their original
            // paths so `EmployeesListPage` / `EmployeeFormPage` internal
            // navigation continues to work.
            { index: true, element: <Navigate to="/staff/employees" replace /> },
            {
              path: 'new',
              element: (
                <ProtectedRoute permission="addEmployee">
                  {suspense(<EmployeeFormPage />)}
                </ProtectedRoute>
              ),
            },
            // Legacy bulk-import / record / history paths now redirect into
            // the Staff hub. Bookmarks and saved links keep working.
            { path: 'import', element: <Navigate to="/staff/import" replace /> },
            { path: 'import-zk', element: <Navigate to="/staff/import-zk" replace /> },
            { path: 'records', element: <Navigate to="/staff/records" replace /> },
            { path: 'schedule-history', element: <Navigate to="/staff/schedule-history" replace /> },
            {
              path: 'unregistered',
              // Moved into Devices Hub. Old URL kept as a redirect so saved
              // links and emails still land users on the right tab.
              element: <Navigate to="/devices/unregistered" replace />,
            },
            { path: 'summary-report', element: suspense(<EmployeeSnapshotPage />) },
            // Moved into Shifts & Time hub. Keep old URL as a redirect.
            {
              path: 'overtime-slots',
              element: <Navigate to="/work-time/overtime-slots" replace />,
            },
            // Wave I.2.a — ZK ↔ HR intake page now lives inside the Devices
            // hub at `/devices/zk-import`. Old URL kept as a redirect so any
            // bookmark, email link, or in-product CTA keeps landing users on
            // the same surface.
            { path: 'zk-intake', element: <Navigate to="/devices/zk-import" replace /> },
            // `:id` MUST come last among `path: 'X'` siblings so the literal
            // paths above (import, records, etc.) match first.
            {
              path: ':id',
              element: (
                <ProtectedRoute permission="showEmployees">
                  {suspense(<EmployeeFormPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Legacy URLs redirected into the Staff hub.
        { path: 'admin/users', element: <Navigate to="/staff/admin-users" replace /> },
        // Legacy single-page URLs redirected into the Staff hub.
        { path: 'visitors', element: <Navigate to="/visitors" replace /> },
        { path: 'tags', element: <Navigate to="/staff/tags" replace /> },
        {
          path: 'attendance',
          children: [
            // Attendance log page now lives inside the Shifts & Time hub.
            // Keep the old URL as a redirect so bookmarks and external links
            // (emails, prior reports, etc.) continue to work.
            { index: true, element: <Navigate to="/work-time/attendance" replace /> },
            {
              path: 'lectures',
              // Moved into Students Hub. Old URL kept as a redirect.
              element: <Navigate to="/students/lectures" replace />,
            },
          ],
        },
        // Moved into the Shifts & Time hub. Old URL kept as a redirect.
        {
          path: 'sudden-fingerprints',
          element: <Navigate to="/work-time/sudden-fingerprints" replace />,
        },
        // Moved into the Settings hub. Old URL kept as a redirect.
        { path: 'audit-log', element: <Navigate to="/settings/audit-log" replace /> },
        { path: 'requests', element: <Navigate to="/devices/requests" replace /> },
        // QR check-in moved into Students Hub.
        { path: 'qr-attendance', element: <Navigate to="/students/qr" replace /> },
        {
          path: 'students',
          // Tabbed Students Hub — child routes map to URL paths
          // (/students/attendance, /students/reports, /students/lectures,
          // /students/qr, /students/subjects, /students/stages). The hub
          // layout also redirects the legacy `?tab=...` query param to the
          // matching path so old bookmarks continue to work.
          //
          // The parent route is intentionally login-only (no specific
          // permission): users with only `showSubjects` or `showStages`
          // still need to reach this hub to access those tabs. Each child
          // enforces its own permission. Hub gate accepts any students perm.
          element: (
            <ProtectedRoute
              permission={[
                'showStudents',
                'showStages',
                'showSubjects',
                'showStudentAttendance',
                'showStudentDetails',
                'showNotificationsPending',
                'showAbsenceWarnings',
              ]}
            >
              {suspense(<StudentsHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<StudentsOverview />) },
            {
              path: 'attendance',
              element: (
                <ProtectedRoute permission="showStudentAttendance">
                  {suspense(<StudentAttendancePage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'reports',
              element: (
                <ProtectedRoute permission="showStudentAttendance">
                  {suspense(<StudentAttendanceReportPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'lectures',
              element: (
                <ProtectedRoute permission="showStudentAttendance">
                  {suspense(<LectureReportPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'qr',
              element: (
                <ProtectedRoute permission="showStudentAttendance">
                  {suspense(<QrAttendancePage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'subjects',
              element: (
                <ProtectedRoute permission="showSubjects">
                  {suspense(<SubjectsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'stages',
              element: (
                <ProtectedRoute permission="showStages">
                  {suspense(<StagesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'news',
              element: (
                <ProtectedRoute>
                  {suspense(<NewsPage />)}
                </ProtectedRoute>
              ),
            },
            // Students Attendance Summary — moved from /reports/students-summary
            // into the Students hub so all student-facing screens live in one
            // place. The old URL keeps working as a redirect (see below).
            {
              path: 'summary',
              element: (
                <ProtectedRoute permission="showAbsenceWarnings">
                  {suspense(<StudentsReportsSummaryPage />)}
                </ProtectedRoute>
              ),
            },
            // Legacy paths.
            { path: 'attendance-report', element: <Navigate to="/students/reports" replace /> },
          ],
        },
        {
          path: 'devices',
          // Tabbed Devices Hub — child routes map to URL paths
          // (/devices/all, /devices/offline, /devices/matrix, …). The hub
          // layout also redirects the legacy `?tab=...` query param to the
          // matching path so old bookmarks continue to work.
          element: (
            <ProtectedRoute permission={['showDevices', 'showBeacons']}>
              {suspense(<DevicesHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<DevicesOverview />) },
            { path: 'all', element: suspense(<DevicesPage />) },
            { path: 'offline', element: suspense(<DevicesOfflinePage />) },
            { path: 'matrix', element: suspense(<DevicesMatrixPage />) },
            { path: 'users', element: suspense(<DeviceEmployeesPage />) },
            // Wave I.2.a — ZK ↔ HR intake page mounted inside the Devices hub
            // (was previously at `/employees/zk-intake`). Server enforces
            // `biometric.manage`; the page itself surfaces a permission-denied
            // alert when the operator can't run intake actions.
            {
              path: 'zk-import',
              element: (
                <ProtectedRoute permission="showDevices">
                  {suspense(<ZkIntakePage />)}
                </ProtectedRoute>
              ),
            },
            { path: 'unregistered', element: suspense(<UnregisteredEmployeesPage />) },
            {
              path: 'networks',
              element: (
                <ProtectedRoute permission="showDevices">
                  {suspense(<NetworksPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'beacons',
              element: (
                <ProtectedRoute permission="showBeacons">
                  {suspense(<BeaconsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'beacons/attempts',
              element: (
                <ProtectedRoute permission="showBeacons">
                  {suspense(<BeaconAttemptsPage />)}
                </ProtectedRoute>
              ),
            },
            { path: 'operations', element: suspense(<OperationsPage />) },
            { path: 'requests', element: suspense(<ApiRequestsPage />) },
            { path: 'live-map', element: suspense(<LiveLocationsPage />) },
            { path: 'watcher', element: suspense(<WatcherPage />) },
            // Legacy /devices/<old-name> URL redirects (mapped to the new
            // child paths). The list page used to live under `/devices/employees`.
            { path: 'employees', element: <Navigate to="/devices/users" replace /> },
          ],
        },
        // Standalone legacy URLs that used to live outside /devices. Keep
        // them as redirects so existing browser tabs / bookmarks keep working.
        { path: 'live-locations', element: <Navigate to="/devices/live-map" replace /> },
        { path: 'watcher',        element: <Navigate to="/devices/watcher" replace /> },
        {
          // Device User Balancing — standalone page (kept out of the Devices hub
          // so the operations report can use the full page width). Gated on the
          // same `showDevices` permission as the rest of the devices surface.
          path: 'device-balancing',
          element: (
            <ProtectedRoute permission="showDevices">
              {suspense(<DeviceBalancingPage />)}
            </ProtectedRoute>
          ),
        },
        {
          path: 'rules',
          element: (
            <ProtectedRoute permission="showRules">
              {suspense(<RulesHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<RulesOverviewPage />) },
            { path: 'attendance', element: suspense(<AttendanceRulesPage />) },
            { path: 'attendance/:id', element: suspense(<AttendanceRuleFormPage />) },
            { path: 'groups', element: suspense(<RuleGroupsPage />) },
            { path: 'groups/:id', element: suspense(<RuleGroupFormPage />) },
            { path: 'alerts', element: suspense(<AlertConditionsPage />) },
            { path: 'generic', element: suspense(<GenericRulesPage />) },
            { path: 'flexible', element: suspense(<FlexibleAggregateRulesPage />) },
            { path: 'event-overrides', element: suspense(<EventOverrideRulesPage />) },
            { path: 'enroll-swap', element: suspense(<EnrollSwapRulesPage />) },
            {
              path: 'projects',
              element: (
                <ProtectedRoute permission="showProject">
                  {suspense(<ProjectsPage />)}
                </ProtectedRoute>
              ),
            },
            // These pages were moved to the Attendance Management hub.
            // Redirect old URLs so bookmarks keep working.
            {
              path: 'extra-minutes',
              element: <Navigate to="/attendance-management/extra-minutes" replace />,
            },
            {
              path: 'time-based-leaves',
              element: <Navigate to="/attendance-management/time-based-leaves" replace />,
            },
            { path: 'guide', element: suspense(<RulesGuidePage />) },
            // Action Policies ("إجراءات وشروط") — a TAB inside the Rules hub.
            // Inherits the hub's `showRules` gate (like its sibling tabs); the
            // backend still enforces requireAccess('actionPolicies', …) on the API.
            { path: 'actions', element: suspense(<ActionPoliciesListPage />) },
            { path: 'actions/new', element: suspense(<ActionPolicyBuilderPage />) },
            { path: 'actions/:id/edit', element: suspense(<ActionPolicyBuilderPage />) },
            { path: 'actions/:id/runs', element: suspense(<ActionPolicyRunsPage />) },
            { path: 'actions/:id/ledger', element: suspense(<ActionPolicyLedgerPage />) },
            // Raises & Promotions ("الترقيات والزيادات") — a TAB inside the Rules
            // hub (mirrors Action Policies). Backend enforces requireAccess('promotions').
            { path: 'promotions', element: suspense(<PromotionsListPage />) },
            { path: 'promotions/new', element: suspense(<PromotionBuilderPage />) },
            { path: 'promotions/:id/edit', element: suspense(<PromotionBuilderPage />) },
            { path: 'promotions/inbox', element: suspense(<PromotionInboxPage />) },
          ],
        },
        {
          path: 'recruitment',
          element: (
            <ProtectedRoute permission="recruitment.view">
              {suspense(<RecruitmentHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<RecruitmentOverview />) },
            { path: 'forms', element: suspense(<FormsListPage />) },
            { path: 'forms/new', element: suspense(<FormBuilderPage />) },
            { path: 'forms/:id', element: suspense(<FormBuilderPage />) },
            { path: 'applicants', element: suspense(<ApplicationsBoardPage />) },
            { path: 'tags', element: suspense(<ApplicantTagsPage />) },
          ],
        },
        {
          // Visitor management hub (passes + entry log). Top-level module.
          path: 'visitors',
          element: (
            <ProtectedRoute permission="showVisitors">
              {suspense(<VisitorsHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<VisitPassesPage />) },
            { path: 'entries', element: suspense(<VisitorEntriesPage />) },
          ],
        },
        {
          path: 'supervisor-requests',
          element: (
            <ProtectedRoute permission="supervisorRequests.view">
              {suspense(<SupervisorRequestsPage />)}
            </ProtectedRoute>
          ),
        },
        {
          path: 'complaints',
          element: (
            <ProtectedRoute permission="complaints.view">
              {suspense(<ComplaintsPage />)}
            </ProtectedRoute>
          ),
        },
        {
          path: 'notification-templates',
          element: (
            <ProtectedRoute permission="sendBroadcast">
              {suspense(<NotificationTemplatesPage />)}
            </ProtectedRoute>
          ),
        },
        {
          path: 'surveys',
          element: (
            <ProtectedRoute permission="surveys.view">{suspense(<SurveysListPage />)}</ProtectedRoute>
          ),
        },
        {
          path: 'surveys/new',
          element: (
            <ProtectedRoute permission="surveys.create">{suspense(<SurveyBuilderPage />)}</ProtectedRoute>
          ),
        },
        {
          path: 'surveys/:id',
          element: (
            <ProtectedRoute permission="surveys.view">{suspense(<SurveyStatsPage />)}</ProtectedRoute>
          ),
        },
        {
          path: 'surveys/:id/edit',
          element: (
            <ProtectedRoute permission="surveys.edit">{suspense(<SurveyBuilderPage />)}</ProtectedRoute>
          ),
        },
        {
          // Employee Evaluation hub (تقييم الموظفين). Hub gate accepts any of the
          // evaluation permissions so a user holding only `settings` or
          // `manageTypes` still reaches the hub; each child enforces its own.
          path: 'evaluation',
          element: (
            <ProtectedRoute
              permission={['evaluation.view', 'evaluation.manageTypes', 'evaluation.settings']}
            >
              {suspense(<EvaluationHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: <Navigate to="/evaluation/leaderboard" replace /> },
            {
              path: 'leaderboard',
              element: (
                <ProtectedRoute permission="evaluation.view">
                  {suspense(<EvaluationLeaderboardPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'records',
              element: (
                <ProtectedRoute permission="evaluation.view">
                  {suspense(<EvaluationRecordsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'journey',
              element: (
                <ProtectedRoute permission="evaluation.view">
                  {suspense(<EvaluationJourneyPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'letters',
              element: (
                <ProtectedRoute permission="evaluation.view">
                  {suspense(<EvaluationLettersPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'templates',
              element: (
                <ProtectedRoute permission="evaluation.manageTypes">
                  {suspense(<EvaluationTemplatesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'event-types',
              element: (
                <ProtectedRoute permission="evaluation.manageTypes">
                  {suspense(<EvaluationEventTypesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'settings',
              element: (
                <ProtectedRoute permission="evaluation.settings">
                  {suspense(<EvaluationSettingsPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Legacy redirects: Action Policies moved into the Rules hub as a tab
        // (/rules/actions). Keep any old /app/actions deep links working.
        { path: 'actions', element: <Navigate to="/rules/actions" replace /> },
        { path: 'actions/*', element: <Navigate to="/rules/actions" replace /> },
        {
          path: 'shifts',
          children: [
            // List moved into the Shifts & Time hub. Keep the new/edit form
            // pages at their original paths so internal navigations
            // (e.g. ShiftsListPage links to `/shifts/${id}`) keep working.
            { index: true, element: <Navigate to="/work-time/shifts" replace /> },
            {
              path: 'new',
              element: (
                <ProtectedRoute permission="showShifts">
                  {suspense(<ShiftFormPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: ':id',
              element: (
                <ProtectedRoute permission="showShifts">
                  {suspense(<ShiftFormPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: 'time-slots',
          children: [
            // List moved into the Shifts & Time hub. Keep new/edit form pages
            // at their original paths so internal navigation continues to work.
            { index: true, element: <Navigate to="/work-time/time-slots" replace /> },
            {
              path: 'new',
              element: (
                <ProtectedRoute permission="showTimeSlot">
                  {suspense(<TimeSlotFormPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: ':id',
              element: (
                <ProtectedRoute permission="showTimeSlot">
                  {suspense(<TimeSlotFormPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          // Schedule Exceptions — list lives inside the Shifts & Time hub
          // (/work-time/schedule-exceptions). The form / detail pages keep
          // their own top-level URLs so deep links work cleanly.
          // Permission: TODO — backend currently relies on the standard
          // authMiddleware. When the project introduces a dedicated
          // `schedule_exceptions.manage` key we should wire it through
          // ProtectedRoute. For now the hub gate (`showShifts` family) is
          // sufficient since this lives inside /work-time.
          path: 'schedule-exceptions',
          children: [
            { index: true, element: <Navigate to="/work-time/schedule-exceptions" replace /> },
            {
              path: 'new',
              element: (
                <ProtectedRoute permission={['showShifts', 'showTimeSlot']}>
                  {suspense(<ScheduleExceptionFormPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <ProtectedRoute permission={['showShifts', 'showTimeSlot']}>
                  {suspense(<ScheduleExceptionFormPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: ':id',
              element: (
                <ProtectedRoute permission={['showShifts', 'showTimeSlot']}>
                  {suspense(<ScheduleExceptionDetailPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: 'staff',
          element: (
            <ProtectedRoute
              permission={[
                'showStaffManangement',
                'showEmployees',
                'showUsers',
                'showDepartments',
                'showBranches',
                'showTagEmployee',
                'showEmployeeAttachments',
              ]}
            >
              {suspense(<StaffHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<StaffOverview />) },
            {
              path: 'employees',
              element: (
                <ProtectedRoute permission="showEmployees">
                  {suspense(<EmployeesListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'admin-users',
              element: (
                <ProtectedRoute permission="showUsers">
                  {suspense(<UsersListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'onboarding-links',
              element: (
                <ProtectedRoute permission="showEmployees">
                  {suspense(<OnboardingLinksPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'onboarding-links/:linkId',
              element: (
                <ProtectedRoute permission="showEmployees">
                  {suspense(<OnboardingSubmissionsPage />)}
                </ProtectedRoute>
              ),
            },
            // Visitors promoted to a top-level module. Redirect old bookmarks.
            { path: 'visitors', element: <Navigate to="/visitors" replace /> },
            {
              path: 'tags',
              element: (
                <ProtectedRoute permission="showTagEmployee">
                  {suspense(<TagsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'departments',
              element: (
                <ProtectedRoute permission="showDepartments">
                  {suspense(<DepartmentsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'branches',
              element: (
                <ProtectedRoute permission="showBranches">
                  {suspense(<BranchesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'records',
              element: (
                <ProtectedRoute permission="showEmployeeAttachments">
                  {suspense(<EmployeeRecordsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'custom-fields',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<CustomFieldsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'schedule-history',
              element: (
                <ProtectedRoute>
                  {suspense(<ScheduleHistoryPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'import',
              element: (
                <ProtectedRoute>
                  {suspense(<EmployeeImportExcelPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'import-zk',
              element: (
                <ProtectedRoute>
                  {suspense(<EmployeeImportZkPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: 'work-time',
          element: (
            <ProtectedRoute
              permission={[
                'showTimeManagment',
                'showShifts',
                'showTimeSlot',
                'showAttendance',
                'showAttLog',
                'showSuddenlyCheckReport',
              ]}
            >
              {suspense(<WorkTimeHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<WorkTimeOverview />) },
            {
              path: 'attendance',
              element: (
                <ProtectedRoute permission="showAttendance">
                  {suspense(<AttendancePage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'shifts',
              element: (
                <ProtectedRoute permission="showShifts">
                  {suspense(<ShiftsListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'time-slots',
              element: (
                <ProtectedRoute permission="showTimeSlot">
                  {suspense(<TimeSlotsListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              // Schedule Exceptions list — the form/detail pages keep their
              // own top-level URLs at /schedule-exceptions/{new|:id|:id/edit}.
              path: 'schedule-exceptions',
              element: (
                <ProtectedRoute permission={['showShifts', 'showTimeSlot']}>
                  {suspense(<ScheduleExceptionsListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'schedule-templates',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<ScheduleTemplatesListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'overtime-slots',
              element: (
                <ProtectedRoute>
                  {suspense(<EmployeeOvertimeSlotsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'realtime',
              element: (
                <ProtectedRoute permission="showSuddenlyCheckReport">
                  {suspense(<RealtimePage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'sudden-fingerprints',
              element: (
                <ProtectedRoute permission="showSuddenFingerprint">
                  {suspense(<SuddenFingerprintsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'notifications-pending',
              element: (
                <ProtectedRoute permission="showNotificationsPending">
                  {suspense(<NotificationsPendingPage />)}
                </ProtectedRoute>
              ),
            },
            {
              // Device punches rejected by the geofence check (GPS outside the
              // employee's allowed zone). Read-only audit log — gated on the
              // same attendance permission as the attendance log itself.
              path: 'location-violations',
              element: (
                <ProtectedRoute permission="showAttendance">
                  {suspense(<LocationViolationsPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        // Legacy URLs — preserved as redirects so old bookmarks/emails keep
        // working. The actual pages now live inside the Attendance Management
        // hub at /attendance-management/* below.
        { path: 'leaves', element: <Navigate to="/attendance-management/leaves" replace /> },
        { path: 'leave-types', element: <Navigate to="/attendance-management/leave-types" replace /> },
        {
          path: 'attendance-management',
          element: (
            <ProtectedRoute
              permission={[
                'showWorkManagement',
                'showLeaves',
                'showLeavesTypes',
                'showOfficialHolidays',
                'showRewardsPenalties',
                'showExtraMinutes',
                'showPartialTimeRequest',
              ]}
            >
              {suspense(<AttendanceManagementHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<AttendanceManagementOverview />) },
            {
              path: 'leaves',
              element: (
                <ProtectedRoute permission="showLeaves">
                  {suspense(<LeavesListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'leave-types',
              element: (
                <ProtectedRoute permission="showLeavesTypes">
                  {suspense(<LeaveTypesListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'holidays',
              element: (
                <ProtectedRoute permission="showOfficialHolidays">
                  {suspense(<HolidaysPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'time-based-leaves',
              element: (
                <ProtectedRoute permission="showPartialTimeRequest">
                  {suspense(<TimeBasedLeavesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'time-leave-types',
              element: (
                <ProtectedRoute permission="showPartialTimeRequest">
                  {suspense(<TimeLeaveTypesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'time-leave-settings',
              element: (
                <ProtectedRoute permission="showPartialTimeRequest">
                  {suspense(<TimeLeaveSettingsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'extra-minutes',
              element: (
                <ProtectedRoute permission="showExtraMinutes">
                  {suspense(<ExtraMinutesPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'rewards',
              element: (
                <ProtectedRoute permission="showRewardsPenalties">
                  {suspense(<RewardsPenaltiesPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
        // The Organization hub was removed — every page it used to host now
        // lives in a more relevant hub (Staff / Students / Rules). The old
        // /organization/* URLs are kept as redirects so bookmarks, the
        // Dashboard quick-links, and any external references continue to
        // land users on the right page.
        { path: 'organization', element: <Navigate to="/staff" replace /> },
        {
          path: 'organization/departments',
          element: <Navigate to="/staff/departments" replace />,
        },
        {
          path: 'organization/branches',
          element: <Navigate to="/staff/branches" replace />,
        },
        {
          path: 'organization/projects',
          element: <Navigate to="/rules/projects" replace />,
        },
        {
          path: 'organization/subjects',
          element: <Navigate to="/students/subjects" replace />,
        },
        {
          path: 'organization/stages',
          element: <Navigate to="/students/stages" replace />,
        },
        {
          path: 'reports',
          element: (
            <ProtectedRoute
              permission={[
                'showReports',
                'showAttReport',
                'showAttLogDetails',
                'showMonthlyAttendanceReport',
                'showCapacityReport',
                'showEmpSummeryReport',
                'showSuddenlyCheckReport',
                'showSalarySlips',
                'showRealtimeAttend',
                'showReportLayouts',
              ]}
            >
              {suspense(<ReportsHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<ReportsOverview />) },
            {
              path: 'attendance',
              element: (
                <ProtectedRoute permission="showAttReport">
                  {suspense(<MasterAttendanceReportPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'attendance-log-details',
              element: (
                <ProtectedRoute permission="showAttLogDetails">
                  {suspense(<DetailedAttendanceLogPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'monthly',
              element: (
                <ProtectedRoute permission="showMonthlyAttendanceReport">
                  {suspense(<MonthlyAttendanceReportPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'capacity',
              element: (
                <ProtectedRoute permission="showCapacityReport">
                  {suspense(<DepartmentCapacityReportPage />)}
                </ProtectedRoute>
              ),
            },
            // Students Attendance Summary moved into the Students hub at
            // `/students/summary`. Old URL kept as a redirect so any saved
            // bookmark or cross-link still lands on the page.
            { path: 'students-summary', element: <Navigate to="/students/summary" replace /> },
            {
              path: 'employee-summary',
              element: (
                <ProtectedRoute permission="showEmpSummeryReport">
                  {suspense(<EmployeeSummaryReportPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'salaries',
              element: (
                <ProtectedRoute permission="showSuddenlyCheckReport">
                  {suspense(<SalarySlipsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'loans',
              element: (
                <ProtectedRoute permission="loans.view">
                  {suspense(<LoansReportPage />)}
                </ProtectedRoute>
              ),
            },
            // Rewards & Penalties has moved to the Attendance Management hub.
            // Keep the old URL as a redirect for any bookmarks/links.
            {
              path: 'rewards',
              element: <Navigate to="/attendance-management/rewards" replace />,
            },
          ],
        },
        {
          path: 'reports/:slug/print',
          element: <ProtectedRoute>{suspense(<PrintViewPage />)}</ProtectedRoute>,
        },
        { path: 'admin', element: <Navigate to="/dashboard" replace /> },
        {
          // Forbidden landing — any authenticated user can reach it. The
          // `<ProtectedRoute>` wrapper without a `permission` prop only
          // enforces the auth check, so this never loops.
          path: 'forbidden',
          element: <ProtectedRoute>{suspense(<ForbiddenPage />)}</ProtectedRoute>,
        },
        {
          // Billing is part of the always-visible base (إدارة الحضور,
          // القواعد, الفواتير, حالة النظام). It must surface for every
          // authenticated admin regardless of `showBilling`, so the
          // permission gate has been removed. Backend access control on the
          // billing API stays in place.
          path: 'billing',
          element: <ProtectedRoute>{suspense(<BillingPage />)}</ProtectedRoute>,
        },
        {
          path: 'settings',
          // Hub gate accepts any settings-related permission so users with
          // only `showAuditLog` (no `showPublicSettings`) can still reach
          // /settings/audit-log. `showEmployees` is included because the
          // Visitors tab moved here from /staff and uses that permission.
          // Each child enforces its own permission.
          element: (
            <ProtectedRoute
              permission={['showPublicSettings', 'showAuditLog', 'showEmployees']}
            >
              {suspense(<SettingsHubLayout />)}
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: suspense(<SettingsOverview />) },
            {
              // Visitors promoted to a top-level module; keep old bookmarks working.
              path: 'visitors',
              element: <Navigate to="/visitors" replace />,
            },
            {
              path: 'company',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<CompanySettingsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'notifications',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<NotificationsPage />)}
                </ProtectedRoute>
              ),
            },
            // Schedule templates moved to /work-time/schedule-templates.
            // Keep the old URL as a redirect for any saved bookmarks.
            {
              path: 'schedule-templates',
              element: <Navigate to="/work-time/schedule-templates" replace />,
            },
            // Backup page is hidden from the navigation but kept reachable
            // by direct URL (e.g. external bookmarks or admin tooling).
            {
              path: 'backup',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<BackupPage />)}
                </ProtectedRoute>
              ),
            },
            // Custom fields moved to /staff/custom-fields. Old URL kept as
            // a redirect so saved bookmarks and the legacy banner keep working.
            {
              path: 'custom-fields',
              element: <Navigate to="/staff/custom-fields" replace />,
            },
            // Holidays moved to /attendance-management/holidays. Old URL kept
            // as a redirect for any saved bookmarks / legacy banner links.
            {
              path: 'holidays',
              element: <Navigate to="/attendance-management/holidays" replace />,
            },
            {
              path: 'ai',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<AiTenantSettingsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'pdf-designs',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<PdfDesignsListPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'pdf-designs/new',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<PdfDesignFormPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'pdf-designs/:id',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<PdfDesignFormPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'translations',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<TranslationsPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'audit-log',
              element: (
                <ProtectedRoute permission="showAuditLog">
                  {suspense(<AuditLogPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'webhook-log',
              element: (
                <ProtectedRoute permission="showAuditLog">
                  {suspense(<InboundWebhookLogPage />)}
                </ProtectedRoute>
              ),
            },
            {
              path: 'legacy-import',
              element: (
                <ProtectedRoute permission="showPublicSettings">
                  {suspense(<LegacyImportPage />)}
                </ProtectedRoute>
              ),
            },
          ],
        },
      ],
    },
    { path: '*', element: <NotFound /> },
  ],
  { basename: '/app' },
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
